/**
 * Data loading utilities for runtime
 */

import type { RpmData, RpmObject, WorldMap, Tile, Location } from '@/qud/types';

/**
 * Terrain display name definitions (loaded separately)
 */
export interface TerrainDefs {
  [blueprintName: string]: {
    displayName: string;
    description: string;
  };
}

/**
 * Compressed RPM object: either just an index (number) or [index, {intProperties}]
 */
export type CompressedObject = number | [number, Record<string, number>];

/**
 * Compressed RPM data with string deduplication
 */
export interface CompressedRpmData {
  width: number;
  height: number;
  cells: Record<string, CompressedObject[]>;
}

/**
 * Bundle of all RPMs with shared string table
 */
export interface RpmBundle {
  strings: string[];
  rpms: Record<string, CompressedRpmData>;
}

// Cached bundle to avoid reloading
let cachedBundle: RpmBundle | null = null;

/**
 * Load the compressed RPM bundle
 */
export async function loadRpmBundle(): Promise<RpmBundle> {
  if (cachedBundle) return cachedBundle;

  const response = await fetch('/data/rpms.json');
  if (!response.ok) {
    throw new Error(`Failed to load RPM data: ${response.statusText}`);
  }
  cachedBundle = await response.json();
  return cachedBundle!;
}

/**
 * Decompress a single RPM from the bundle
 */
function decompressRpm(bundle: RpmBundle, compressed: CompressedRpmData): RpmData {
  const cells: Record<string, RpmObject[]> = {};

  for (const [key, compressedObjs] of Object.entries(compressed.cells)) {
    cells[key] = compressedObjs.map((obj) => {
      if (typeof obj === 'number') {
        return { name: bundle.strings[obj] };
      }
      const [idx, intProperties] = obj;
      return { name: bundle.strings[idx], intProperties };
    });
  }

  return { width: compressed.width, height: compressed.height, cells };
}

/**
 * Load a single RPM by name (decompressed)
 */
export async function loadRpmData(rpmName: string = 'QudWorldMap'): Promise<RpmData> {
  const bundle = await loadRpmBundle();
  const compressed = bundle.rpms[rpmName];
  if (!compressed) {
    throw new Error(`RPM not found: ${rpmName}`);
  }
  return decompressRpm(bundle, compressed);
}

/**
 * Load all RPM names from the bundle
 */
export async function getRpmNames(): Promise<string[]> {
  const bundle = await loadRpmBundle();
  return Object.keys(bundle.rpms);
}

/**
 * Load terrain definitions for display names
 */
export async function loadTerrainDefs(): Promise<TerrainDefs> {
  const response = await fetch('/data/terrain-defs.json');
  if (!response.ok) {
    throw new Error(`Failed to load terrain definitions: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Build a WorldMap from lean RPM data
 */
export function buildWorldMap(rpm: RpmData, terrainDefs: TerrainDefs): WorldMap {
  const { width, height, cells } = rpm;

  // Initialize tiles array
  const tiles: Tile[][] = [];
  for (let y = 0; y < height; y++) {
    tiles[y] = [];
    for (let x = 0; x < width; x++) {
      const key = `${x},${y}`;
      const objects = cells[key] || [];
      // Extract blueprint names from objects
      const blueprints = objects.map((obj) => obj.name);
      tiles[y][x] = { blueprints };
    }
  }

  // Build locations from terrain definitions
  const locations = new Map<string, Location>();
  for (const [key, objects] of Object.entries(cells)) {
    if (objects.length === 0) continue;

    // Use first blueprint for location info (terrain)
    const terrainBlueprint = objects[0].name;
    const def = terrainDefs[terrainBlueprint];

    locations.set(key, {
      id: key,
      name: def?.displayName || terrainBlueprint,
      description: def?.description || 'A region of Qud.',
    });
  }

  return { width, height, tiles, locations };
}

/**
 * Check if world data is available
 */
export async function checkDataAvailable(): Promise<boolean> {
  try {
    const response = await fetch('/data/rpms.json', { method: 'HEAD' });
    return response.ok;
  } catch {
    return false;
  }
}
